import { useEffect, useState } from 'react'
import { fetchDocuments } from 'db/utils'
import { UploadListingData } from 'db/listings'
import { areAllValuesEmpty } from '@/lib/utils'
import { useGetDirectoryListingLazyQuery } from 'graphql/generated/graphql'
import axios from 'axios'

// Declare the global 'google' object
declare global {
  interface Window {
    google: any
  }
}

interface SearchQuery {
  type: string
  location: string
  placeId?: string
  ingredient: string
  miles: number
}

const getListingCache = (cacheKey: string) => {
  return (
    typeof window !== 'undefined' &&
    JSON.parse(sessionStorage.getItem(cacheKey) || '[]')
  )
}

export function filterByTypeAndIsFeatured(listings: any): [] {
  return (
    listings?.length && listings?.filter((listing: any) => listing.isFeatured)
  )
}

// Lazily injects the Google Maps JS SDK the first time it's actually needed
// (only getCoordinatesFromAddress below uses window.google.maps in this whole
// app — everywhere else calls Google's plain REST APIs) instead of loading it
// globally on every page via a <script> tag in app/layout.tsx.
let googleMapsLoadPromise: Promise<void> | null = null

function ensureGoogleMapsLoaded(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Maps can only load in the browser'))
  }
  if (window.google?.maps) return Promise.resolve()
  if (googleMapsLoadPromise) return googleMapsLoadPromise

  googleMapsLoadPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_MAP_KEY}&loading=async`
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => {
      googleMapsLoadPromise = null
      reject(new Error('Failed to load Google Maps script'))
    }
    document.head.appendChild(script)
  })

  return googleMapsLoadPromise
}

// Function to geocode an address using the Google Maps Geocoding API
async function getCoordinatesFromAddress(
  address: string,
  city: string,
  state: string
): Promise<{ latitude: number; longitude: number }> {
  await ensureGoogleMapsLoaded()
  const fullAddress = `${address}, ${city}, ${state}`
  const geocoder = new window.google.maps.Geocoder()

  return new Promise<{ latitude: number; longitude: number }>(
    (resolve, reject) => {
      geocoder.geocode(
        { address: fullAddress },
        (results: any, status: any) => {
          if (status === window.google.maps.GeocoderStatus.OK && results[0]) {
            const location = results[0].geometry?.location
            resolve({ latitude: location.lat(), longitude: location.lng() })
          } else {
            reject(
              new Error(
                `Geocode was not successful for the following reason: ${status}`
              )
            )
          }
        }
      )
    }
  )
}


export async function getCoordinatesFromPlaceId(placeId: string): Promise<{
  latitude: number
  longitude: number
}> {
  const res = await axios.get(`/api/google-places/place?placeId=${placeId}`)
  return {
    latitude: res.data.place.geometry?.location.lat,
    longitude: res.data.place.geometry?.location.lng,
  }
}

const IP_LOCATION_CACHE_KEY = 'culineer_ip_location'

// Create a variable to store the user location once it's fetched
let cachedUserLocation: {
  latitude: number
  longitude: number
  city?: string
  state?: string
  source?: 'gps' | 'ip'
} | null = null

export function clearUserLocationCache() {
  cachedUserLocation = null
  if (typeof window !== 'undefined') {
    try { sessionStorage.removeItem(IP_LOCATION_CACHE_KEY) } catch {}
  }
}

async function getLocationFromIP(): Promise<{ latitude: number; longitude: number; city?: string; state?: string }> {
  // Check sessionStorage cache to avoid hammering ipapi.co rate limits
  try {
    const cached = typeof window !== 'undefined' ? sessionStorage.getItem(IP_LOCATION_CACHE_KEY) : null
    if (cached) {
      const data = JSON.parse(cached)
      if (data.latitude && data.longitude) {
        return { latitude: data.latitude, longitude: data.longitude, city: data.city, state: data.state }
      }
    }
  } catch {}

  try {
    // Without a timeout, a slow/rate-limited upstream geo-IP lookup leaves
    // this hanging indefinitely — every page gated on getUserLocation()
    // (Discover, /display producers & markets) sits on a spinner forever.
    const res = await fetch('/api/location', { signal: AbortSignal.timeout(5000) })
    if (!res.ok) return { latitude: 0, longitude: 0 }
    const data = await res.json()
    if (data.latitude && data.longitude) {
      try { sessionStorage.setItem(IP_LOCATION_CACHE_KEY, JSON.stringify(data)) } catch {}
      return { latitude: data.latitude, longitude: data.longitude, city: data.city, state: data.state }
    }
  } catch {}
  return { latitude: 0, longitude: 0 }
}

// Function to get the user's location using the Geolocation API
// Falls back to IP-based location if GPS is denied or unavailable
export async function getUserLocation(): Promise<{
  latitude: number
  longitude: number
  city?: string
  state?: string
  source?: 'gps' | 'ip'
}> {
  const options = {
    enableHighAccuracy: true,
    timeout: 5000,
    maximumAge: 0,
  }

  // Check if the location is already cached
  if (cachedUserLocation) {
    return Promise.resolve(cachedUserLocation)
  }

  // If not cached, fetch the location
  return new Promise((resolve) => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            source: 'gps' as const,
          }
          cachedUserLocation = userLocation
          resolve(userLocation)
        },
        async () => {
          // GPS denied or error — fall back to IP-based location
          const ipLocation = await getLocationFromIP()
          const result = { ...ipLocation, source: 'ip' as const }
          cachedUserLocation = result
          resolve(result)
        },
        options
      )
    } else {
      // Geolocation not supported — fall back to IP-based location
      getLocationFromIP().then(ipLocation => {
        const result = { ...ipLocation, source: 'ip' as const }
        cachedUserLocation = result
        resolve(result)
      })
    }
  })
}

// export async function getUserLocation(): Promise<{
//   latitude: number
//   longitude: number
// }> {
//   const options = {
//     enableHighAccuracy: true,
//     timeout: 5000, // Maximum time (in milliseconds) to wait for location data
//     maximumAge: 0, // Maximum age (in milliseconds) of a cached location
//   }
//   // Check if the location is already cached
//   if (cachedUserLocation) {
//     return Promise.resolve(cachedUserLocation)
//   }

//   // If not cached, fetch the location
//   return new Promise<{ latitude: number; longitude: number }>(
//     (resolve, reject) => {
//       if ('geolocation' in navigator) {
//         navigator?.geolocation.getCurrentPosition(
//           (position) => {
//             const userLocation = {
//               latitude: position.coords.latitude,
//               longitude: position.coords.longitude,
//             }
//             // Cache the location
//             cachedUserLocation = userLocation

//             resolve(userLocation)
//           },
//           (error) => {
//             reject(error)
//           },
//           options
//         )
//       } else {
//         reject(new Error('Geolocation is not available in this browser.'))
//       }
//     }
//   )
// }

export function isTypeMatch(listing: any, searchQuery: SearchQuery): boolean {
  const { type } = searchQuery
  return !type || listing.user_type?.toLowerCase().includes(type?.toLowerCase())
}

export function isCityMatch(listing: any, searchQuery: SearchQuery): boolean {
  const city = searchQuery.location.split(', ')
  return listing.city?.toLowerCase().includes(city[0].toLowerCase())
}

async function isLocationMatch(
  listingCoordinates: { latitude: number; longitude: number },
  queryCoordinates: { latitude: number; longitude: number },
  miles: number
): Promise<boolean> {
  try {
    // Calculate the distance between the user's location and the listing's location
    const distance = calculateDistance(
      queryCoordinates?.latitude,
      queryCoordinates?.longitude,
      listingCoordinates?.latitude,
      listingCoordinates?.longitude
    )

    return distance <= miles
  } catch (error) {
    console.error('Error retrieving coordinates:', error)
    return false
  }
}

// Helper function to calculate the distance between two latitude and longitude points
export function calculateDistance(
  lat1: number | undefined,
  lon1: number | undefined,
  lat2: number | undefined,
  lon2: number | undefined
): number {
  // Handle cases where latitude or longitude is undefined
  if (
    lat1 === undefined ||
    lon1 === undefined ||
    lat2 === undefined ||
    lon2 === undefined
  ) {
    return Number.POSITIVE_INFINITY // Return a large value for undefined coordinates
  }

  const R = 6371 // Radius of the Earth in kilometers
  const dLat = (lat2 - lat1) * (Math.PI / 180)
  const dLon = (lon2 - lon1) * (Math.PI / 180)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  const distance = R * c
  // Convert distance to miles
  return distance * 0.621371
}

export function isIngredientMatch(listing: any, searchQuery: SearchQuery): boolean {
  const { ingredient } = searchQuery
  const query = ingredient?.toLowerCase() || ''
  if (!query) return false

  const searchTerms = query.split(',').map((term) => term.trim())
  return (
    !ingredient ||
    searchTerms.some((term) =>
      listing.ingredients.some((ing: any) =>
        ing.name.toLowerCase().includes(term)
      )
    )
  )
}

// Function to add coordinates to a location object
export async function addCoordinatesToLocation(location: any) {
  try {
    // Use the getCoordinatesFromAddress function to add coordinates
    const coordinates = await getCoordinatesFromAddress(
      location.address,
      location.city,
      location.state
    )

    // Create a new location object with coordinates
    const newLocation = {
      ...location,
      coordinates,
    }

    return newLocation
  } catch (error) {
    console.error('Error adding coordinates to location:', error)
    throw error
  }
}

// Function to fetch listings, add coordinates, and update them using UploadListingData
export async function fetchAddCoordinatesAndUpdateListings() {
  try {
    // Fetch the listings from Firestore
    const listings = await fetchDocuments('listings')

    // Filter the listings that don't have coordinates
    // const listingsWithoutCoordinates = listings.filter(
    //   (listing) => !listing?.location?.coordinates
    // )
    const listingsWithoutCoordinates = listings.filter((listing) => {
      return listing && listing.latitude === undefined && listing.longitude === undefined;
    });
    
    // Process each listing to add coordinates to the location object and update it
    for (const listing of listingsWithoutCoordinates) {
      const { location } = listing

      try {
        const newLocation = await addCoordinatesToLocation(location)

        if (newLocation) {
          const updatedListing = {
            ...listing,
            location: newLocation,
          }

          const id = listing.id 

          await UploadListingData(updatedListing, id)
        } else {
          console.error(
            'Geocode was not successful for the following location:',
            location
          )
        }
      } catch (error) {
        console.error('Error adding coordinates to location:', error)
      }
    }
  } catch (error) {
    console.error('Error updating listings without coordinates:', error)
  }
}

export const sortByType = (a: any, b: any) => {
  if (a?.metadata?.type === 'Premium' && b?.metadata?.type !== 'Premium') {
    return -1
  } else if (
    a?.metadata?.type !== 'Premium' &&
    b?.metadata?.type === 'Premium'
  ) {
    return 1
  } else {
    return 0
  }
}

// export function calculateHaversineDistance(
//   lat1: number,
//   lon1: number,
//   lat2: number,
//   lon2: number
// ) {
//   const earthRadiusMiles = 3958.8 // Radius of the Earth in miles
//   const dLat = ((lat2 - lat1) * Math.PI) / 180
//   const dLon = ((lon2 - lon1) * Math.PI) / 180
//   const lat1Rad = (lat1 * Math.PI) / 180
//   const lat2Rad = (lat2 * Math.PI) / 180

//   const a =
//     Math.sin(dLat / 2) ** 2 +
//     Math.sin(dLon / 2) ** 2 * Math.cos(lat1Rad) * Math.cos(lat2Rad)
//   const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

//   const distanceMiles = earthRadiusMiles * c
//   return distanceMiles
// }

// export function calculateHaversineDistance(lat1:number, lon1:number, lat2:number, lon2:number) {
//   const R = 3958.8; // Earth radius in miles

//   const dLat = toRadians(lat2 - lat1);
//   const dLon = toRadians(lon2 - lon1);

//   const a =
//     Math.sin(dLat / 2) * Math.sin(dLat / 2) +
//     Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

//   const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

//   const distance = R * c; // Distance in miles
//   return distance;
// }

// function toRadians(degrees:number) {
//   return degrees * (Math.PI / 180);
// }

export function calculateHaversineDistance(lat1:number, lon1:number, lat2:number, lon2:number) {
  var R = 6371; // Radius of the earth in km
  var dLat = deg2rad(lat2-lat1);  // deg2rad below
  var dLon = deg2rad(lon2-lon1); 
  var a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
    Math.sin(dLon/2) * Math.sin(dLon/2)
    ; 
  var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
  var d = R * c; // Distance in km
  let miles = d * 0.621371
  return miles;
}

function deg2rad(deg:number) {
  return deg * (Math.PI/180)
}


export async function getStateAndCountryFromLatLng(
  latitude: number,
  longitude: number
): Promise<{ state: string; country: string }> {
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAP_KEY!; // Replace with your API key
  const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`;

  try {
    const response = await fetch(url);
    const data = await response.json();

    if (data.status !== 'OK') {
      throw new Error(`Geocoding failed: ${data.status}`);
    }

    const addressComponents = data.results[0].address_components;

    let state = '';
    let country = '';

    for (const component of addressComponents) {
      if (component.types.includes('administrative_area_level_1')) {
        state = component.long_name;
      } else if (component.types.includes('country')) {
        country = component.long_name;
      }
    }

    return { state, country };
  } catch (error) {
    console.error('Error fetching location data:', error);
    return { state: '', country: '' }; // Return empty strings on error
  }
}

